import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useState } from "react";
import { useHealth, useMe } from "../lib/api";
import { useTheme } from "../lib/theme";
import { FeedbackLink, SessionBanner } from "./SessionBanner";

type NavItem = {
  to: string;
  label: string;
  also?: string[];
  roles?: Array<"admin" | "editor" | "viewer">;
};
const NAV: NavItem[] = [
  { to: "/", label: "Map" },
  { to: "/table", label: "Table" },
  { to: "/explore", label: "Explore", also: ["/charts", "/states"] },
  { to: "/lists", label: "Lists" },
  { to: "/review", label: "Review" },
  { to: "/dc", label: "Data centers" },
  { to: "/settings", label: "Settings", roles: ["admin"] },
  { to: "/admin/ingest", label: "Ingest", roles: ["admin"] },
] as const;

export function Shell({ children }: { children: ReactNode }) {
  const me = useMe();
  const role = me.data?.role;
  // admin-only pages are hidden from editors and viewers; the API still enforces the role
  const nav = NAV.filter((n) => !n.roles || (role && n.roles.includes(role)));
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [theme, setTheme] = useTheme();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const health = useHealth();

  function submitSearch(e: React.FormEvent) {
    e.preventDefault();
    const term = q.trim();
    void navigate({
      to: "/table",
      search: (prev) => ({ ...prev, q: term || undefined, page: 1 }),
    });
  }

  return (
    <div
      className="flex h-full flex-col"
      style={{ background: "var(--ink)", color: "var(--paper)" }}
    >
      <header
        className="flex h-11 shrink-0 items-center gap-5 border-b px-3"
        style={{ borderColor: "var(--line)", background: "var(--panel)" }}
      >
        <Link to="/" className="flex items-center gap-2 no-underline hover:no-underline">
          <img
            src="/brand/plenum-mark.svg"
            alt="Plenum"
            width={22}
            height={22}
            className="rounded-[5px]"
          />
          <span
            className="text-[13px] tracking-[0.28em] uppercase"
            style={{ color: "var(--gold)" }}
          >
            Plenum
          </span>
          <span className="hidden text-[11px] lg:inline" style={{ color: "var(--muted)" }}>
            UpStream prospect map
          </span>
        </Link>
        <nav className="flex items-center gap-0.5" aria-label="Primary">
          {nav.map((item) => {
            const also: readonly string[] = item.also ?? [];
            const active =
              item.to === "/"
                ? pathname === "/" || pathname.startsWith("/sites")
                : pathname === item.to ||
                  pathname.startsWith(`${item.to}/`) ||
                  also.some((p) => pathname === p || pathname.startsWith(`${p}/`));
            return (
              <Link
                key={item.to}
                to={item.to}
                className="rounded px-2 py-1 no-underline hover:no-underline"
                style={{
                  color: active ? "var(--paper)" : "var(--muted)",
                  background: active ? "var(--panel-3)" : "transparent",
                }}
                aria-current={active ? "page" : undefined}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>
        <form onSubmit={submitSearch} className="ml-auto flex items-center gap-2">
          <input
            className="input w-56"
            placeholder="Find site, operator, EIA id"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Search sites"
          />
        </form>
        <span
          className="num hidden text-[11px] xl:inline"
          style={{ color: "var(--muted-2)" }}
          title="API version · layer version"
        >
          {health.data ? `v${health.data.version} · layer ${health.data.layer_version ?? "—"}` : ""}
        </span>
        <FeedbackLink version={health.data?.version} layer={health.data?.layer_version} />
        {me.data ? (
          <span className="flex items-center gap-1" data-testid="account">
            <span
              className="hidden max-w-[220px] truncate text-[11px] xl:inline"
              style={{ color: "var(--muted)" }}
              title={`${me.data.role}${me.data.org.name ? ` · ${me.data.org.name}` : ""}`}
            >
              {me.data.email}
            </span>
            {health.data?.logout_url ? (
              <a
                className="btn btn-ghost btn-sm"
                href={health.data.logout_url}
                data-testid="sign-out"
              >
                Sign out
              </a>
            ) : null}
          </span>
        ) : null}
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          aria-label="Toggle colour theme"
        >
          {theme === "dark" ? "Light" : "Dark"}
        </button>
      </header>
      <SessionBanner />
      <div className="min-h-0 flex-1">{children}</div>
    </div>
  );
}
